/* oxlint-disable grafit-oxlint/no-tailwind-font-classes -- Typography 2 migration */
import { Button } from "@grafit/design-system/components/molecules/button";
import { Input } from "@grafit/design-system/components/molecules/input";
import { Label } from "@grafit/design-system/components/molecules/label";
import { toast } from "@grafit/design-system/components/molecules/toast";
import { Trans, useTranslation } from "@grafit/i18n";
import * as React from "react";
import { authClient } from "../auth-client";
import { assertOnline } from "../utils/assert-online";
import { storeLastLoginDate } from "../utils/store-last-login";

interface PasswordSignInProps {
  email: string;
  disabled: boolean;
  isOnboarding: boolean;
}

export const PasswordSignIn = React.memo<PasswordSignInProps>(({ email, disabled, isOnboarding }) => {
  const { t } = useTranslation();
  const passwordId = React.useId();
  const [password, setPassword] = React.useState("");
  const [isPending, setIsPending] = React.useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setIsPending(true);

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password || !assertOnline(t)) {
      setIsPending(false);
      return;
    }

    const callbackURL = isOnboarding ? `${window.location.origin}/onboarding` : window.location.origin;

    try {
      await authClient.signIn
        .email({
          email: trimmedEmail,
          password,
          rememberMe: true,
          callbackURL,
        })
        .then((v) => {
          if (v.error) {
            throw new Error(v.error.message);
          }
          storeLastLoginDate(trimmedEmail);
          return v;
        });
      window.location.replace(callbackURL);
    } catch {
      toast.error(t((r) => r["auth.sign-in.error.generic"]));
      setIsPending(false);
    }
  }

  return (
    // oxlint-disable-next-line jsx-no-new-function-as-prop
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" method="post">
      <div className="space-y-2">
        <Label htmlFor={passwordId}>
          <Trans i18nKey="auth.sign-in.password.label" />
        </Label>
        <Input
          id={passwordId}
          name="password"
          type="password"
          value={password}
          // oxlint-disable-next-line jsx-no-new-function-as-prop
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t((r) => r["auth.sign-in.password.placeholder"])}
          autoComplete="current-password"
          required
          disabled={disabled || isPending}
        />
        <div className="text-right">
          <a href="/reset-password" className="text-neutral-900 text-sm hover:underline">
            <Trans i18nKey="auth.sign-in.password.forgot" />
          </a>
        </div>
      </div>
      <Button
        variant={{ kind: "primary", renderMode: "fill", size: "m" }}
        type="submit"
        className="w-full"
        isPending={isPending}
        disabled={isPending || !email.trim() || !password}
        enableCheckmark
      >
        <Trans i18nKey="auth.sign-in.password.button.sign-in" />
      </Button>
    </form>
  );
});
PasswordSignIn.displayName = "PasswordSignIn";
